/**
 * PillarHub — authoritative topic-cluster hub page for a content pillar.
 * Routes: /ai-business-intelligence, /ai-consulting, /ai-for-australian-business
 * Fetches cluster articles (matched by focus terms) from /api/public/pillars/:slug
 * and renders a hero, guide grid, FAQ (with FAQPage schema) and related pages.
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { API_URL } from '@/config/apiConfig';
import { ArrowRight, BookOpen, Sparkles, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PillarHub() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+|\/+$/g, '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setErr(false);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/pillars/${slug}`);
        if (!res.ok) throw new Error('not found');
        const d = await res.json();
        if (active) setData(d);
      } catch {
        if (active) setErr(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="pillar-loading">Loading…</div>;
  }
  if (err || !data?.pillar) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" data-testid="pillar-notfound">
        <p className="text-slate-600">This guide isn’t available.</p>
        <Link to="/blog" className="text-teal-700 font-semibold">Browse the blog →</Link>
      </div>
    );
  }

  const { pillar, posts = [] } = data;
  const url = `https://corvana.com.au/${pillar.slug}`;
  const faqSchema = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: (pillar.faqs || []).map((f) => ({
      '@type': 'Question', name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  const breadcrumb = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://corvana.com.au/' },
      { '@type': 'ListItem', position: 2, name: 'Guides', item: 'https://corvana.com.au/guides' },
      { '@type': 'ListItem', position: 3, name: pillar.title, item: url },
    ],
  };

  return (
    <div className="bg-white" data-testid="pillar-hub">
      <Helmet>
        <title>{pillar.title}: The Complete Guide | Corvana</title>
        <meta name="description" content={pillar.tagline} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={`${pillar.title} | Corvana`} />
        <meta property="og:description" content={pillar.tagline} />
        <meta property="og:url" content={url} />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0B1F2A] text-white">
        <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(60% 60% at 20% 10%, #0E7C86 0%, transparent 60%)' }} />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <nav className="flex items-center gap-1 text-xs text-white/60 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/90">{pillar.title}</span>
          </nav>
          <span className="inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.18em] text-teal-300">
            <Sparkles className="w-4 h-4" /> Corvana guide
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]" data-testid="pillar-h1">{pillar.h1}</h1>
          <p className="mt-5 text-lg sm:text-xl text-white/80 max-w-2xl">{pillar.tagline}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact"><Button className="bg-teal-500 hover:bg-teal-400 text-[#06141b] font-bold rounded-full px-6" data-testid="pillar-cta-demo">Book a demo <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
            <Link to="/pricing"><Button variant="outline" className="rounded-full px-6 border-white/30 text-white hover:bg-white/10">See pricing</Button></Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        {(pillar.intro || []).map((para, i) => (
          <p key={i} className="text-lg text-slate-700 leading-relaxed mb-5">{para}</p>
        ))}
      </section>

      {/* Guide grid */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-16">
        <div className="flex items-center gap-2 mb-6">
          <BookOpen className="w-5 h-5 text-teal-700" />
          <h2 className="text-lg md:text-xl font-bold text-slate-900">In this guide{posts.length ? ` · ${posts.length} articles` : ''}</h2>
        </div>
        {posts.length === 0 ? (
          <p className="text-slate-500" data-testid="pillar-empty">New articles for this pillar are being published — check back shortly.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pillar-articles">
            {posts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`pillar-article-${p.slug}`}
                className="group flex flex-col rounded-2xl border border-slate-200 hover:border-teal-400 hover:shadow-lg transition-all overflow-hidden bg-white"
              >
                <div className="relative h-36 bg-gradient-to-br from-teal-600 to-cyan-700 overflow-hidden flex-shrink-0">
                  <span className="absolute inset-0 flex items-center justify-center p-4 text-center font-display font-semibold text-white/85 text-sm" aria-hidden="true">
                    {pillar.title}
                  </span>
                  {p.image_url && (
                    <img
                      src={p.image_url.startsWith('http') ? p.image_url : `https://corvana.com.au${p.image_url}`}
                      alt={p.title}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.remove(); }}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  {p.category && <span className="text-[11px] font-bold uppercase tracking-wide text-teal-700">{p.category}</span>}
                  <h3 className="mt-1 font-bold text-slate-900 leading-snug group-hover:text-teal-700 line-clamp-2">{p.title}</h3>
                  {p.excerpt && <p className="mt-2 text-sm text-slate-500 line-clamp-3">{p.excerpt}</p>}
                  <span className="mt-3 inline-flex items-center text-sm font-semibold text-teal-700">Read <ArrowRight className="w-3.5 h-3.5 ml-1" /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* FAQ */}
      {(pillar.faqs || []).length > 0 && (
        <section className="bg-slate-50 py-16">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            <h2 className="text-2xl font-black text-slate-900 mb-8">Frequently asked questions</h2>
            <div className="space-y-6" data-testid="pillar-faqs">
              {pillar.faqs.map((f, i) => (
                <div key={i} className="border-b border-slate-200 pb-6">
                  <h3 className="font-bold text-slate-900 mb-2">{f.q}</h3>
                  <p className="text-slate-600 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related pages — internal linking */}
      {(pillar.related || []).length > 0 && (
        <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14">
          <h2 className="text-lg font-bold text-slate-900 mb-5">Explore related solutions</h2>
          <div className="flex flex-wrap gap-3" data-testid="pillar-related">
            {pillar.related.map((r) => (
              <Link key={r.href} to={r.href} className="inline-flex items-center gap-2 rounded-full border border-slate-200 hover:border-teal-400 hover:bg-teal-50 px-5 py-2.5 text-sm font-semibold text-slate-800 transition-colors">
                {r.label} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="bg-[#0B1F2A] text-white py-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-black">See {pillar.title.toLowerCase()} on your own data</h2>
          <p className="mt-3 text-white/70">Connect your tools and get live dashboards, forecasts and alerts in days.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/contact"><Button className="bg-teal-500 hover:bg-teal-400 text-[#06141b] font-bold rounded-full px-7">Book a demo</Button></Link>
            <Link to="/blog"><Button variant="outline" className="rounded-full px-7 border-white/30 text-white hover:bg-white/10">Read the blog</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
